import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { Wallet, Upload, Home, MessageCircle, User, Settings, Sparkles } from 'lucide-react'
import HarmonyHubLogo from './HarmonyHubLogo'
import HDHLogo from './HDHLogo'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { address, isConnected, connect, disconnect } = useWallet()
  const location = useLocation()

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const isHDH = location.pathname.startsWith('/hdh')

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to={isHDH ? "/hdh" : "/"} className="flex items-center">
              {isHDH ? (
                <HDHLogo size="md" variant="full" className="text-gray-900" />
              ) : (
                <HarmonyHubLogo size="md" variant="full" className="text-gray-900" />
              )}
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex space-x-8">
              {!isHDH ? (
                <>
                  <Link
                    to="/"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive('/') 
                        ? 'text-purple-600 bg-purple-50' 
                        : 'text-gray-700 hover:text-purple-600 hover:bg-gray-50'
                    }`}
                  >
                    <Home className="w-4 h-4" />
                    <span>Home</span>
                  </Link>
                  <Link
                    to="/upload"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive('/upload') 
                        ? 'text-purple-600 bg-purple-50' 
                        : 'text-gray-700 hover:text-purple-600 hover:bg-gray-50'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    <span>Upload</span>
                  </Link>
                  <Link
                    to="/messages"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive('/messages') 
                        ? 'text-purple-600 bg-purple-50' 
                        : 'text-gray-700 hover:text-purple-600 hover:bg-gray-50'
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>Messages</span>
                  </Link>
                  <Link
                    to="/hdh"
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                  >
                    <HDHLogo size="sm" variant="icon" />
                    <span>Data Hub</span>
                  </Link>
                </>
              ) : (
                <Link
                  to="/"
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-gray-700 hover:text-purple-600 hover:bg-gray-50"
                >
                  <HarmonyHubLogo size="sm" variant="icon" />
                  <span>Back to Hub</span>
                </Link>
              )}
            </nav>

            {/* Wallet Connection & Profile */}
            <div className="flex items-center space-x-4">
              {isConnected ? (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium">
                      {address?.slice(0, 6)}...{address?.slice(-4)}
                    </span>
                  </div>
                  
                  {/* Profile Dropdown */}
                  <div className="relative group">
                    <button className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50 transition-colors">
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </button>
                    
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="py-1">
                        <Link
                          to="/create-profile"
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          <span>Create Profile</span>
                        </Link>
                        <Link
                          to="/u/demo" // Demo profile for now
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          <span>View Demo Profile</span>
                        </Link>
             <Link
               to="/upload"
               className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
             >
               <Upload className="w-4 h-4" />
               <span>Upload Content</span>
             </Link>
             <Link
               to="/template-marketplace"
               className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
             >
               <Sparkles className="w-4 h-4" />
               <span>Template Marketplace</span>
             </Link>
                        <hr className="my-1" />
                        <button
                          onClick={disconnect}
                          className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Wallet className="w-4 h-4" />
                          <span>Disconnect</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/create-profile"
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-purple-600 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>Create Profile</span>
                  </Link>
                  <button
                    onClick={connect}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Wallet className="w-4 h-4" />
                    <span>Connect Wallet</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                {isHDH ? (
                  <HDHLogo size="md" variant="full" className="text-gray-900" />
                ) : (
                  <HarmonyHubLogo size="md" variant="full" className="text-gray-900" />
                )}
              </div>
              <p className="text-gray-600 mb-4">
                Decentralized content platform powered by Sui, Walrus, and Seal encryption.
              </p>
              <div className="flex space-x-4">
                <span className="text-sm text-gray-500">Powered by</span>
                <span className="text-sm font-semibold text-purple-600">Sui</span>
                <span className="text-sm font-semibold text-blue-600">Walrus</span>
                <span className="text-sm font-semibold text-green-600">Seal</span>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Platform</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-gray-600 hover:text-purple-600">Home</Link></li>
                <li><Link to="/upload" className="text-sm text-gray-600 hover:text-purple-600">Upload</Link></li>
                <li><Link to="/messages" className="text-sm text-gray-600 hover:text-purple-600">Messages</Link></li>
                <li><Link to="/create-profile" className="text-sm text-gray-600 hover:text-purple-600">Create Profile</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-purple-600">Documentation</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-purple-600">API</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-purple-600">Support</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center">
            <p className="text-sm text-gray-500">
              © 2024 HarmonyDataHub. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
